import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProfileComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { AlignLeft, AlignCenter, AlignRight, Upload, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

interface ProfileSettingsProps {
  component: ProfileComponent;
}

export const ProfileSettings: React.FC<ProfileSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUpdate = (updates: Partial<ProfileComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('settings.common.imageInvalidType'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('settings.common.imageTooLarge'));
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const token = localStorage.getItem('token');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/upload/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || t('settings.common.uploadFailed'));
      }

      const data = await response.json();
      handleUpdate({ avatarUrl: data.url });
      toast.success(t('settings.profile.uploadSuccess'));
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || t('settings.profile.uploadFailedSpecific'));
    } finally {
      setIsUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.profile.styleLabel')}</label>
        <select
          value={component.settings.style || 'default'}
          onChange={(e) => handleUpdate({ style: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="default">{t('settings.common.styles.default')}</option>
          <option value="card">{t('settings.common.styles.cardShadow')}</option>
          <option value="minimal">{t('settings.common.styles.minimal')}</option>
          <option value="modern">{t('settings.common.styles.modern')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.profile.avatarImage')}</label>
        
        {/* Image preview */}
        {component.settings.avatarUrl && (
          <div className="mb-3">
            <img
              src={component.settings.avatarUrl}
              alt="Avatar preview"
              className="w-24 h-24 rounded-full object-cover border-2 border-gray-300"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=Avatar&size=200&background=667eea&color=fff';
              }}
            />
          </div>
        )}

        {/* Upload button */}
        <div className="flex gap-2 mb-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                {t('settings.common.uploading')}
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                {t('settings.common.uploadImage')}
              </>
            )}
          </button>
          <span className="text-xs text-gray-400 flex items-center">
            {t('settings.profile.sizeHint')}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {t('settings.profile.formatHint')}
        </p>

        <div className="mt-2">
          <label className="block text-xs text-gray-400 mb-1">{t('settings.common.orEnterImageUrl')}</label>
          <input
            type="text"
            value={component.settings.avatarUrl}
            onChange={(e) => handleUpdate({ avatarUrl: e.target.value })}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 break-all"
            placeholder="https://example.com/avatar.jpg"
            title={component.settings.avatarUrl}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.profile.avatarSize')}</label>
        <input
          type="range"
          min="60"
          max="200"
          value={component.settings.avatarSize}
          onChange={(e) => handleUpdate({ avatarSize: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-400 mt-1">{component.settings.avatarSize}px</div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.alignment')}</label>
        <div className="flex gap-2">
          <button
            onClick={() => handleUpdate({ alignment: 'left' })}
            className={`flex-1 p-2 rounded ${component.settings.alignment === 'left' ? 'bg-blue-600 text-white' : 'bg-gray-800'}`}
          >
            <AlignLeft size={18} className="mx-auto" />
          </button>
          <button
            onClick={() => handleUpdate({ alignment: 'center' })}
            className={`flex-1 p-2 rounded ${component.settings.alignment === 'center' ? 'bg-blue-600 text-white' : 'bg-gray-800'}`}
          >
            <AlignCenter size={18} className="mx-auto" />
          </button>
          <button
            onClick={() => handleUpdate({ alignment: 'right' })}
            className={`flex-1 p-2 rounded ${component.settings.alignment === 'right' ? 'bg-blue-600 text-white' : 'bg-gray-800'}`}
          >
            <AlignRight size={18} className="mx-auto" />
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.padding')}</label>
        <input
          type="range"
          min="0"
          max="80"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-400 mt-1">{component.settings.padding}px</div>
      </div>
    </div>
  );
};

